import { useSyncExternalStore } from 'react'

export type ToastTone = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
  id: number
  tone: ToastTone
  title: string
  description?: string
}

type Listener = () => void

let toasts: ToastItem[] = []
let nextId = 1
const listeners = new Set<Listener>()

function emit(): void {
  for (const l of listeners) l()
}

export function pushToast(toast: Omit<ToastItem, 'id'>): number {
  const id = nextId++
  toasts = [...toasts.slice(-4), { ...toast, id }]
  emit()
  return id
}

export function dismissToast(id: number): void {
  toasts = toasts.filter((t) => t.id !== id)
  emit()
}

/** Imperative API usable from anywhere (mutations, interceptors, event handlers). */
export const toast = {
  success: (title: string, description?: string) => pushToast({ tone: 'success', title, description }),
  error: (title: string, description?: string) => pushToast({ tone: 'error', title, description }),
  info: (title: string, description?: string) => pushToast({ tone: 'info', title, description }),
  warning: (title: string, description?: string) => pushToast({ tone: 'warning', title, description }),
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => toasts,
  )
}
